import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getNotifications,
  clientActiveSubscription,
  markNotificationAsRead,
  companyProfile,
  getClientCurrentSubscription,
  getAllClientJobs,
} from "../../services/clientCalls";
import { errorHandler } from "../../utils";
import Breadcrumb from "../../components/UI/Breadcrumb/Breadcrumb";
import WelcomeBanner from "./WelcomeBanner";
import styles from "./ClientActivitySummary.module.css";
import ActivitySummary from "./ActivitySummary";
import QuickActions from "./QuickActions";
import NotificationsFeed from "./NotificationsFeed";
import ProfileCompletionCard from "../../components/UI/Client/CompanyProfile/ProfileCompletionCard";
import SubscriptionStatusCard from "./SubscriptionStatusCard";
import FullScreenLoader from "../../components/Loaders/FullScreen/loader";

const getClientNotificationContent = (notification) => {
  const { type, title, message } = notification;
  const extractJobTitle = (msg) => {
    const match = msg?.match(/applied to your (.+) job/i);
    return match ? match[1] : null;
  };
  if (type === "JOB_APPLICATION") {
    const jobTitle = extractJobTitle(message) || "job";
    return {
      title: `New ${jobTitle} Application`,
      message: `Hola! A talent just applied for your ${jobTitle} position.`,
      link: `/talent?tab=matches&view=list`,
      showActionLink: true,
      icon: "IconBriefcase",
    };
  }
  const titleLower = title?.toLowerCase() || "";
  if (titleLower.includes("talent applied for job")) {
    const jobTitle = extractJobTitle(message) || "position";
    return {
      title: `${jobTitle} Application`,
      message: `Hola! A skilled professional has applied for your ${jobTitle} role.`,
      link: "talent?tab=matches&view=list",
      showActionLink: true,
      icon: "IconBriefcase",
    };
  }
  if (titleLower.includes("matched with some talents")) {
    return {
      title: "Multiple Matches Found",
      message: "Hola! You have just matched with multiple talents.",
      link: "/client/talents?tab=matches",
      showActionLink: true,
      icon: "IconUsers",
    };
  }
  if (titleLower.includes("payment was successful")) {
    return {
      title: "Payment Successful",
      message:
        "Your payment was successful. You can now match with 5 talents, valid for 1 year.",
      link: "/client/talents?tab=matches",
      showActionLink: true,
      icon: "IconAward",
    };
  }
  return {
    title: title || "Notification",
    message: message?.replace(/<[^>]*>/g, "") || "You have a new notification.",
    link: "/client/dashboard",
    showActionLink: false,
    icon: "IconMessageCircle",
  };
};

const mapNotificationsToFeed = (notifications = []) => {
  return notifications.map((notification, index) => {
    const content = getClientNotificationContent(notification);

    return {
      id: notification._id || notification.id || `notification-${index}`,
      icon: content.icon,
      title: content.title,
      message: content.message,
      link: content.link,
      showActionLink: content.showActionLink,
      time: new Date(notification.createdAt).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      isRead: notification.isRead,
    };
  });
};

function ClientActivitySummary({ type }) {
  const [notifications, setNotifications] = useState([]);
  const [activitySummaryData, setActivitySummaryData] = useState(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalJobs, setTotalJobs] = useState(0);

  const getProfile = async () => {
    try {
      setLoading(true);
      const res = await companyProfile();
      if (res?.data?.data?.client) setData(res?.data?.data?.client);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getProfile();
    fetchAllJobs();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const onlyNotifications =
    new URLSearchParams(location.search).get("onlyNotifications") === "true";

  const fetchNotifications = async (pageNum = 1) => {
    try {
      const res = await getNotifications(pageNum);
      if (res?.data?.data?.notifications) {
        const data = res?.data?.data;
        setNotifications(data?.notifications);
        setTotalItems(data?.total || 0);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const fetchClientActivitySummary = async () => {
    try {
      const res = await getClientCurrentSubscription();
      if (res?.data?.data?.data) {
        setActivitySummaryData(res?.data?.data?.data);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    }
  };

  const fetchAllJobs = async () => {
    setLoading(true);
    try {
      const res = await getAllClientJobs(page, "active");
      if (res?.data?.data?.totalItems) {
        setTotalJobs(res.data.data.totalItems ?? 0);
      }
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler(
            "Network error: Something went wrong, Please check your internet connection and try again.",
            "Error",
          );
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification, link) => {
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === notification.id ? { ...n, isRead: true } : n,
          ),
        );
      } catch {
        errorHandler("Failed to mark notification as read");
      }
    }
    if (link) navigate(link);
  };

  const autoMarkAsRead = async (notification) => {
    try {
      await markNotificationAsRead(notification?.id);
      setNotifications((prev) =>
        prev.map((n) =>
          n?._id === notification?.id ? { ...n, isRead: true } : n,
        ),
      );
    } catch {
      errorHandler("Failed to auto-mark notification as read");
    }
  };

  useEffect(() => {
    fetchNotifications(page);
    if (!onlyNotifications) fetchClientActivitySummary();
  }, [page]);

  return (
    <>
      {loading && <FullScreenLoader />}
      <Breadcrumb crumbs={[]} title="Dashboard" />
      <WelcomeBanner companyName={data?.companyName} />

      <ActivitySummary
        stats={activitySummaryData}
        totalJobs={totalJobs}
      />

      <div className={styles.grid}>
        <div className={styles.left}>
          {notifications && (
            <NotificationsFeed
              notifications={mapNotificationsToFeed(notifications)}
              onNotificationClick={handleNotificationClick}
              autoMarkAsRead={autoMarkAsRead}
            />
          )}
        </div>

        <div className={styles.right}>
          <ProfileCompletionCard
            profileData={data}
            getCompanyProfile={getProfile}
            showLink
          />
          {/* <RecentTalentActivityCard talents={recentTalents} /> */}
          <SubscriptionStatusCard subscription={activitySummaryData} />
        </div>
      </div>
    </>
  );
}

export default ClientActivitySummary;
